import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Share2, MessageCircle, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatAmount } from '@/utils/formatAmount';

interface ShareButtonProps {
  displayName: string;
  amount: number;
  rank: number;
  message?: string;
  currency?: string;
}

// Custom X (Twitter) icon
const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const ShareButton = ({ displayName, amount, rank, currency = 'EUR' }: ShareButtonProps) => {
  const { t, i18n } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const currentLang = i18n.language?.startsWith('de') ? 'de' : 'en';

  // Format amount with currency
  const formattedAmount = formatAmount(amount, currency);

  // Generate share text using i18n
  const shareText = t('share.tweetText', { 
    rank, 
    amount: formattedAmount 
  });

  const openShareDialog = () => {
    setIsDialogOpen(true);
    setCopied(false);
  };

  const shareToTwitter = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
  };

  const shareToWhatsApp = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(whatsappUrl, '_blank');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success(currentLang === 'de' ? 'Text kopiert!' : 'Text copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(currentLang === 'de' ? 'Kopieren fehlgeschlagen' : 'Copy failed');
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={openShareDialog}
        className="h-8 w-8 p-0 hover:bg-primary/20 hover:text-primary"
        title={t('leaderboard.share')}
      >
        <Share2 className="h-4 w-4" />
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="terminal-border bg-background max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="terminal-glow text-center text-lg sm:text-xl">
              {currentLang === 'de' ? '[ TEILEN ]' : '[ SHARE ]'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Share Text Preview */}
            <div className="terminal-border p-3 sm:p-4 bg-muted/30">
              <p className="text-xs sm:text-sm text-foreground whitespace-pre-line font-mono">
                {shareText}
              </p>
            </div>
            
            {/* Share Buttons */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <Button
                onClick={shareToTwitter}
                className="terminal-border text-xs sm:text-sm flex-col h-auto py-3"
                variant="outline"
              >
                <XIcon className="h-5 w-5 mb-1" />
                <span className="text-[10px] sm:text-xs">{t('share.twitter')}</span>
              </Button>
              <Button
                onClick={shareToWhatsApp}
                className="terminal-border text-xs sm:text-sm flex-col h-auto py-3"
                variant="outline"
              >
                <MessageCircle className="h-5 w-5 mb-1" />
                <span className="text-[10px] sm:text-xs">{t('share.whatsapp')}</span>
              </Button>
              <Button
                onClick={copyToClipboard}
                className="terminal-border text-xs sm:text-sm flex-col h-auto py-3"
                variant="outline"
              >
                {copied ? (
                  <Check className="h-5 w-5 mb-1 text-primary" />
                ) : (
                  <Copy className="h-5 w-5 mb-1" />
                )}
                <span className="text-[10px] sm:text-xs">
                  {currentLang === 'de' ? 'KOPIEREN' : 'COPY'}
                </span>
              </Button>
            </div>
            
            <p className="text-[10px] sm:text-xs text-muted-foreground text-center">
              {currentLang === 'de' 
                ? '// Zeig der Welt was du getan hast' 
                : '// Show the world what you did'}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareButton;
